import React, { useRef } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Html } from "@react-three/drei";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDesktop, faEye } from "@fortawesome/free-solid-svg-icons";
import { cameraSettings as Camera } from "../data/cameraSettings";
import { Vector3 } from "three";
import IndoorPlant from "./plant";
import Monstera from "./monstera";
import Desktop from "./desktop";
import Piano from "./piano";

const ThreeRender = (props) => {
  const { camera } = useThree();
  const [isFreeCamera, setIsFreeCamera] = React.useState(true);
  const [hovered, setHovered] = React.useState(false);
  const screenRef = useRef();
  const pianoRef = useRef();
  const [onScreen, setOnScreen] = React.useState(false);
  const [onPiano, setOnPiano] = React.useState(false);

  React.useEffect(() => {
    camera.position.lerp(Camera.freePos, 1);
  }, []);

  const changeCameraSettings = () => {
    setIsFreeCamera(!isFreeCamera);
    if (!isFreeCamera) {
      camera.position.lerp(Camera.freePos, 1);
      camera.lookAt(Camera.defaultLookAt);
    } else {
      camera.position.lerp(Camera.defaultPos, 1);
      camera.lookAt(Camera.defaultLookAt);
    }
  };

  React.useEffect(() => {
    if (hovered) {
      document.body.style.cursor = "pointer";
    } else {
      document.body.style.cursor = "default";
    }
  }, [hovered]);

  useFrame((state, delta) => {
    if (isFreeCamera) {
      if (onScreen) {
        screenRef.current && camera.lookAt(Camera.lookAtScreen);
        camera.position.lerp(Camera.posScreen, 0.1);
        camera.updateProjectionMatrix();
      } else {
        const mouse = state.mouse;
        const width = state.size.width;
        const height = state.size.height;
        const x = mouse.x * width;
        const y = mouse.y * height;
        const xMax = width / 2;
        const yMax = height / 2;
        const xRatio = x / xMax;
        const yRatio = y / yMax;
        const xPosition = xRatio * 1;
        const yPosition = yRatio * 0.4 + 0.2;
        camera.position.lerp(new Vector3(xPosition, yPosition, 2), 0.01);
        camera.updateProjectionMatrix();
        camera.lookAt(camera.position.x, camera.position.y, 0.03);
      }
    }
  });

  return (
    <>
      <pointLight position={[10, 10, 10]} intensity={1.5} />
      <ambientLight intensity={1} />
      <Html fullscreen>
        <div className="absolute top-10 left-10 p-4 z-[1000] pointer-events-auto">
          {" "}
          <div className="bg-black bg-opacity-60 rounded-lg p-3 backdrop-blur-sm">
            <p className="text-white font-bold text-lg mb-1">Fabien Lapert</p>
            <p className="text-white text-sm mb-3">Développeur full stack</p>
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => {
                  changeCameraSettings();
                }}
                className="bg-black bg-opacity-60 hover:bg-opacity-80 p-3 rounded-md text-white transition-colors duration-200 flex items-center space-x-2 ui-btn"
                onPointerEnter={() => setHovered(true)}
                onPointerLeave={() => setHovered(false)}
              >
                <FontAwesomeIcon
                  icon={isFreeCamera ? faDesktop : faEye}
                  color="white"
                />
                <span className="text-sm">
                  {isFreeCamera ? "Vue fixe" : "Vue libre"}
                </span>
              </button>
              <p className="text-white text-xs opacity-75">
                {isFreeCamera
                  ? "Bougez votre souris pour explorer"
                  : "Contrôles de caméra fixes"}
              </p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 right-10 p-4 z-[1000] pointer-events-auto">
          <button
            onClick={props.onSwitchMode}
            className="bg-black bg-opacity-60 hover:bg-opacity-80 px-4 py-2 rounded-full text-white text-sm font-medium backdrop-blur-sm transition-colors duration-200 ui-btn"
          >
            Changer de mode d'affichage
          </button>
        </div>
      </Html>
      <group position={[0, -4, 0]} rotation={[0, 0, 0]}>
        <Monstera setLoading={props.setLoading} loading={props.loading} />
        {props.loading >= 1 && (
          <Desktop
            hovered={hovered}
            setHovered={setHovered}
            screenRef={screenRef}
            setOnScreen={setOnScreen}
            onScreen={onScreen}
            setLoading={props.setLoading}
            loading={props.loading}
          />
        )}
        {props.loading >= 2 && (
          <IndoorPlant setLoading={props.setLoading} loading={props.loading} />
        )}
        {props.loading >= 3 && (
          <Piano
            setLoading={props.setLoading}
            loading={props.loading}
            pianoRef={pianoRef}
            onPiano={onPiano}
            setOnPiano={setOnPiano}
          />
        )}
      </group>
      <ContactShadows
        position={[0, -3.6, 0]}
        scale={20}
        blur={2}
        far={4.5}
        opacity={0.6}
      />
      <OrbitControls
        makeDefault
        enablePan={!isFreeCamera}
        enableZoom={false}
        enableRotate={!isFreeCamera}
        dampingFactor={0.05}
        enableDamping={true}
      />
    </>
  );
};

export default ThreeRender;
